/**
 * React Query layer for vocabulary feature.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { primeFileSignedUrlCache } from "@/api/services/global/file";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { LoginRoutes } from "@/routes/config";
import { vocabularyService } from "@/infrastructure/services";
import { TQuizAnswerRequest, TVocabularyWord } from "@/infrastructure/adapters/vocabulary/vocabulary.types";
import i18n from "@/i18n";

/** Only fire queries on authenticated routes (not login/register pages) */
const isAuthenticatedRoute = () => !LoginRoutes.some((route) => window.location.pathname.startsWith(route));

/** Query: Fetch vocabulary categories */
export const useVocabularyCategoriesQuery = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [QUERY_KEYS.VOCABULARY.CATEGORIES, i18n.language],
    queryFn: () => vocabularyService.getCategories(),
    enabled: isAuthenticatedRoute() && (options?.enabled ?? true),
    staleTime: 30 * 1000,
  });
};

/** Hand BE-signed image URLs to the signed-url cache so word cards skip the per-image API call */
const toSignedUrlEntries = (words: TVocabularyWord[]) => words.map((w) => ({ filePath: w.imageFile, signedUrl: w.imageUrl }));

/** Query: Fetch vocabulary word list (single source of truth — summary derived in hook) */
export const useVocabularyWordsQuery = (category?: string, options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [QUERY_KEYS.VOCABULARY.WORDS, i18n.language, category],
    queryFn: async () => {
      const words = await vocabularyService.getWords(category);
      primeFileSignedUrlCache(queryClient, toSignedUrlEntries(words));
      return words;
    },
    enabled: isAuthenticatedRoute() && (options?.enabled ?? true),
    staleTime: 30 * 1000,
    retry: 0,
  });
};

/** Query: Fetch practice session (limited words from BE).
 *  gcTime: 0 ensures cache is discarded on unmount so re-entering the page always fetches fresh words. */
export const useVocabularyPracticeQuery = (limit: number, category?: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [QUERY_KEYS.VOCABULARY.PRACTICE_SESSION, limit, category],
    queryFn: async () => {
      const session = await vocabularyService.getPracticeSession(limit, category);
      primeFileSignedUrlCache(queryClient, toSignedUrlEntries(session.words));
      return session;
    },
    gcTime: 0,
    retry: 0,
  });
};

/** Mutation: Update vocabulary progress (API 2) */
export const useUpdateProgressMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vocabularyId: number) => vocabularyService.updateProgress(vocabularyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.VOCABULARY.WORDS] });
    },
  });
};

/** Mutation: Create quiz session (API 3) */
export const useCreateQuizSessionMutation = () => {
  return useMutation({
    mutationFn: ({ limit, category }: { limit: number; category?: string }) => vocabularyService.createQuizSession(limit, category),
  });
};

/** Mutation: Submit quiz answer (API 4) */
export const useSubmitAnswerMutation = () => {
  return useMutation({
    mutationFn: ({ quizSessionId, req }: { quizSessionId: string; req: TQuizAnswerRequest }) => vocabularyService.submitAnswer(quizSessionId, req),
  });
};

/** Query: Fetch quiz result (API 5) */
export const useQuizResultQuery = (quizSessionId: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.VOCABULARY.QUIZ_RESULT, quizSessionId],
    queryFn: () => vocabularyService.getQuizResult(quizSessionId!),
    enabled: !!quizSessionId,
  });
};
